import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { NEWS_REFRESH_MS } from "../../../lib/liveNews";
import { fetchClient } from "../services/clientsService";
import type { ClientRecord } from "../types/clients.types";

/**
 * `missing` is its own outcome rather than a kind of failure: a 404 is the
 * server answering, and the screen has something definite to say about it.
 * Everything else - offline, 403, 500 - is `failed`, and worth retrying.
 */
export type ClientLoadStatus = "loading" | "ready" | "missing" | "failed";

export interface ClientState {
  /** The client, once it has arrived. `undefined` in every other state. */
  client: ClientRecord | undefined;
  status: ClientLoadStatus;
  /** What the failed state's retry calls. */
  reload: () => void;
}

export interface UseClientOptions {
  /**
   * Whether to keep the record up to date while the screen is open, rather
   * than reading it once on the way in.
   *
   * Off by default, and deliberately: the edit form seeds itself from the
   * record, so a refresh landing mid-sentence would throw away whatever had
   * been typed since the page opened. Only a screen that reads the client
   * should ask for this — `ClientDetailPage` does, because the thing it is
   * waiting for is a pre-consultation form coming back from somebody who is
   * filling it in right now, and a strip that only appears on a hard refresh
   * is not news. See `liveNews`.
   */
  live?: boolean;
}

/**
 * One client, by the `public_id` in the route.
 *
 * Not cached: a client edited elsewhere has to read back correctly on the next
 * visit, and there is no store to invalidate yet.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useClient(
  publicId: string,
  { live = false }: UseClientOptions = {}
): ClientState {
  const { getToken } = useAuth();
  const toast = useToast();

  // Clerk hands back a fresh `getToken` on some renders, and the load below
  // must not re-run for that — a fetch per render is a loop. The effect reads
  // the current one through this rather than depending on its identity.
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  const [client, setClient] = useState<ClientRecord | undefined>(undefined);
  const [status, setStatus] = useState<ClientLoadStatus>("loading");
  // Bumped by `reload`, which is the whole of what re-runs the effect.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Nothing to ask for. Treated as missing rather than left loading, so a
    // route that somehow matched an empty id still resolves to a screen.
    if (!publicId) {
      setClient(undefined);
      setStatus("missing");
      return;
    }

    const controller = new AbortController();

    setStatus("loading");

    fetchClient(() => getTokenRef.current(), publicId, controller.signal)
      .then((loaded) => {
        // Unmounted, or a retry has already replaced this run.
        if (controller.signal.aborted) return;
        setClient(loaded);
        setStatus("ready");
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setClient(undefined);

        // A client that is not there is not an error to report - the screen
        // says so itself, and a toast on top of that is noise.
        if (error instanceof ApiError && error.status === 404) {
          setStatus("missing");
          return;
        }

        setStatus("failed");
        toast.error(
          "Could not load this client",
          error instanceof ApiError
            ? error.message
            : "We could not reach the server. Check your connection and try again."
        );
        console.error("client failed to load:", error);
      });

    return () => controller.abort();
  }, [publicId, toast, attempt]);

  const reload = useCallback(() => setAttempt((current) => current + 1), []);

  // Whether there is a record on screen to quietly replace. Read through a ref
  // by the refresh below so the interval is set up once and does not tear
  // itself down and rebuild every time the record moves.
  const readyRef = useRef(false);
  readyRef.current = status === "ready";

  // Which load the screen is on. A refresh that was in the air when the record
  // was reloaded — an approval, say, or a retry — must not land on top of the
  // answer that reload brought back, so it reads this before and after and
  // throws its own result away if the two disagree.
  const attemptRef = useRef(attempt);
  attemptRef.current = attempt;

  /**
   * A read that nobody sees fail and nobody sees start.
   *
   * Neither `status` nor the toast is touched: this is the screen catching up
   * on its own, and a client that answered a minute ago and cannot be reached
   * now should go on showing what it has rather than replacing a page somebody
   * is reading with an error. The next tick, or the retry the page already
   * offers, is what a real outage runs into.
   *
   * Skipped where there is nothing on screen yet — the load above owns that
   * case, including its skeleton and its failure — and where the tab is not
   * being looked at, since the visibility handler below catches up the moment
   * it is.
   */
  const refresh = useCallback(() => {
    if (!publicId || !readyRef.current) return;
    if (document.visibilityState !== "visible") return;

    const startedOn = attemptRef.current;

    fetchClient(() => getTokenRef.current(), publicId)
      .then((loaded) => {
        if (attemptRef.current !== startedOn) return;
        setClient(loaded);
      })
      .catch(() => {
        /* quiet on purpose — see above */
      });
  }, [publicId]);

  useEffect(() => {
    if (!live) return;

    const timer = window.setInterval(refresh, NEWS_REFRESH_MS);
    // A tab left open for an hour and come back to should be right when it is
    // looked at, not up to a minute later.
    document.addEventListener("visibilitychange", refresh);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [live, refresh]);

  return { client, status, reload };
}
